/**
 * MascotasPage - Listado de mascotas del usuario con plan afiliado y próximo cobro.
 * Más detalle que la tarjeta de Mi cuenta; no reemplaza a /mascotas/:id (detalle completo).
 */

import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, PawPrint, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { PetForm } from '@app/components/forms/PetForm';
import { usePetsList, useCreatePet } from '@modules/pets/presentation/hooks/usePets';
import type { CreatePetRequest } from '@modules/pets/application/dto/PetDTO';
import { useSubscriptions } from '@modules/billing/presentation/hooks/useBilling';
import type { SubscriptionResponse } from '@modules/billing/application/dto/BillingDTO';
import { getErrorMessage, getValidationDetails } from '@shared/infrastructure/http/api.error';
import { cn } from '@shared/utils/cn';

// Referencia solo para mostrar al usuario; el cobro definitivo lo define el backend.
const UF_CLP_APPROX = 37000;

const SPECIES_EMOJI: Record<string, string> = {
  DOG: '🐶', CAT: '🐱', perro: '🐶', gato: '🐱',
};

function speciesEmoji(species?: string) {
  if (!species) return '🐾';
  return SPECIES_EMOJI[species] ?? SPECIES_EMOJI[species.toLowerCase()] ?? '🐾';
}

function speciesLabel(species?: string) {
  if (!species) return '';
  const s = species.toUpperCase();
  if (s === 'DOG') return 'Perro';
  if (s === 'CAT') return 'Gato';
  return species;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusPill(status?: string): { label: string; className: string } {
  switch ((status ?? '').toUpperCase()) {
    case 'ACTIVE':
      return { label: 'Activo', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'PENDING':
      return { label: 'Pendiente', className: 'bg-blue-50 text-blue-700 border-blue-200' };
    case 'PAST_DUE':
      return { label: 'Pago vencido', className: 'bg-amber-50 text-amber-700 border-amber-200' };
    case 'SUSPENDED':
      return { label: 'Suspendido', className: 'bg-violet-50 text-violet-700 border-violet-200' };
    case 'CANCELED':
    case 'CANCELLED':
      return { label: 'Cancelado', className: 'bg-red-50 text-red-600 border-red-200' };
    default:
      return { label: 'Sin plan', className: 'bg-gray-100 text-gray-500 border-gray-200' };
  }
}

/** Prioriza sub abierta; si no hay, toma la última CANCELED por createdAt. */
function pickSubscription(
  subs: SubscriptionResponse[],
  petId: string,
): SubscriptionResponse | null {
  const forPet = subs.filter((s) => s.petId === petId);
  if (forPet.length === 0) return null;
  const order = ['ACTIVE', 'PAST_DUE', 'PENDING', 'SUSPENDED'];
  const open = forPet
    .filter((s) => order.includes((s.status ?? '').toUpperCase()))
    .sort(
      (a, b) =>
        order.indexOf((a.status ?? '').toUpperCase()) -
        order.indexOf((b.status ?? '').toUpperCase()),
    );
  if (open.length > 0) return open[0];
  return [...forPet].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0];
}

export const MascotasPage = () => {
  const { data: pets = [], isLoading: loadingPets, error } = usePetsList();
  const { data: subscriptions = [], isLoading: loadingSubs } = useSubscriptions();
  const createPetMutation = useCreatePet();
  const [addOpen, setAddOpen] = useState(false);

  const subsByPetId = useMemo(() => {
    const map = new Map<string, SubscriptionResponse>();
    for (const pet of pets) {
      const s = pickSubscription(subscriptions, pet.id);
      if (s) map.set(pet.id, s);
    }
    return map;
  }, [pets, subscriptions]);

  const handleCreatePet = async (data: Record<string, unknown>) => {
    const body: CreatePetRequest = {
      name: String(data.name ?? '').trim(),
      species: data.species as 'DOG' | 'CAT',
      breed: (data.breed as string) || undefined,
      birthDate: String(data.birthDate ?? ''),
      gender: (data.gender as string) || undefined,
      color: (data.color as string) || undefined,
      weightKg: typeof data.weightKg === 'number' ? data.weightKg : undefined,
      isSterilized: !!data.isSterilized,
      preExistingConditions: Array.isArray(data.preExistingConditions)
        ? (data.preExistingConditions as string[])
        : [],
      disease: !!data.disease,
      restricted: !!data.restricted,
      minorDisease: !!data.minorDisease,
      tos: !!data.tos,
      microchipNumber: (data.microchipNumber as string) || undefined,
      serial: (data.serial as string) || null,
      couponCode: (data.couponCode as string)?.trim() || null,
    };
    try {
      const result = await createPetMutation.mutateAsync(body);
      toast.success(`${result.pet.name} registrada correctamente.`);
      setAddOpen(false);
    } catch (err) {
      const details = getValidationDetails(err);
      if (details) {
        const msgs = Object.values(details).flat().join('. ');
        toast.error(msgs || 'Revisa los campos marcados.');
      } else {
        toast.error(getErrorMessage(err));
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FFDCE6]">
      <section className="px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl p-6 md:p-10 shadow-lg space-y-6">
            {/* Breadcrumb + Header */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm">
                <Link to="/sucursal-virtual" className="text-gray-500 hover:text-[#FF6F61]">
                  Mi cuenta
                </Link>
                <span className="text-gray-300">/</span>
                <span className="text-gray-800 font-medium">Mis Mascotas</span>
              </div>
              <Button
                className="bg-[#FF6F61] text-white"
                onClick={() => setAddOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar mascota
              </Button>
            </div>

            <div>
              <h1 className="text-3xl font-bold text-gray-900">Mis Mascotas</h1>
              <p className="text-sm text-gray-600 mt-1">
                Gestiona tus mascotas, su plan afiliado y el próximo cobro estimado. Toca una
                tarjeta para ver el detalle y acciones.
              </p>
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50/80 p-4 text-sm text-red-700">
                Error al cargar las mascotas.
              </div>
            )}

            {loadingPets ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-[168px] w-full rounded-2xl" />
                ))}
              </div>
            ) : pets.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 flex flex-col items-center justify-center py-16 px-6 text-center">
                <PawPrint className="h-14 w-14 text-gray-400 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Aún no tienes mascotas registradas
                </h3>
                <p className="text-sm text-gray-600 mb-5 max-w-md">
                  Agrega tu primera mascota para comenzar a protegerla con un plan.
                </p>
                <Button
                  className="bg-[#FF6F61] text-white"
                  onClick={() => setAddOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar primera mascota
                </Button>
              </div>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {pets.map((pet) => {
                  const subscription = subsByPetId.get(pet.id);
                  const status = subscription?.status ?? pet.subscriptionStatus;
                  const pill = statusPill(status);
                  const priceUf = subscription?.finalPriceUf ?? 0;
                  const priceClp = Math.round(priceUf * UF_CLP_APPROX);
                  return (
                    <li key={pet.id}>
                      <Link
                        to={`/mascotas/${pet.id}`}
                        className="group block rounded-2xl bg-white border border-gray-200 overflow-hidden shadow-sm hover:shadow-md hover:border-[#FF6F61]/40 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6F61]"
                      >
                        <div className="flex gap-4 p-4">
                          <div className="w-20 h-20 shrink-0 rounded-xl bg-[#FFDCE6]/50 overflow-hidden flex items-center justify-center">
                            {pet.photoUrl ? (
                              <img
                                src={pet.photoUrl}
                                alt={pet.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-4xl select-none">
                                {speciesEmoji(pet.species)}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">
                                  {pet.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {[speciesLabel(pet.species), pet.breed]
                                    .filter(Boolean)
                                    .join(' · ')}
                                  {pet.age != null
                                    ? ` · ${pet.age} ${pet.age === 1 ? 'año' : 'años'}`
                                    : ''}
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-300 shrink-0 group-hover:text-[#FF6F61] transition-colors" />
                            </div>
                            <span
                              className={cn(
                                'inline-flex items-center mt-2 text-xs font-medium px-2 py-0.5 rounded-full border',
                                pill.className,
                              )}
                            >
                              {pill.label}
                            </span>
                          </div>
                        </div>

                        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/60">
                          {subscription ? (
                            <dl className="grid grid-cols-2 gap-3 text-xs">
                              <div className="min-w-0">
                                <dt className="text-gray-500">Plan</dt>
                                <dd
                                  className="font-medium text-gray-900 truncate"
                                  title={subscription.planName ?? subscription.planId}
                                >
                                  {subscription.planName ?? '—'}
                                </dd>
                              </div>
                              <div className="min-w-0 text-right sm:text-left">
                                <dt className="text-gray-500">Próximo cobro</dt>
                                <dd className="font-medium text-gray-900">
                                  {formatDate(subscription.nextBillingDate)}
                                </dd>
                              </div>
                              <div className="col-span-2 flex items-center justify-between pt-1 border-t border-gray-100">
                                <span className="text-gray-500">Monto estimado</span>
                                <span className="font-semibold text-[#FF6F61] tabular-nums">
                                  {priceUf.toFixed(2)} UF{' '}
                                  <span className="text-gray-400 font-normal text-[11px]">
                                    (~{priceClp.toLocaleString('es-CL')} CLP)
                                  </span>
                                </span>
                              </div>
                            </dl>
                          ) : loadingSubs ? (
                            <Skeleton className="h-10 w-full" />
                          ) : (
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs text-gray-500">Sin plan contratado</p>
                              <span className="text-xs text-[#FF6F61] font-medium">
                                Contratar →
                              </span>
                            </div>
                          )}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Dialog: agregar mascota (formulario inline) */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agregar nueva mascota</DialogTitle>
            <DialogDescription>
              Registra a tu mascota. No implica contratar un plan: podrás hacerlo después desde
              Contratación.
            </DialogDescription>
          </DialogHeader>
          <PetForm
            onSubmit={handleCreatePet}
            onCancel={() => setAddOpen(false)}
            isLoading={createPetMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
